import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';

import {
  CreateDerivativesOrderRequest,
  DerivativesOpenedPositionProjection,
  DerivativesOrderProjection,
  DerivativesOrderSide,
  DerivativesPositionSide,
  TradingPairContractType,
} from 'common/api/autogenerated/trading-gateway';

import { useDerivatives, useDerivativesOrders, useDerivativesPositions } from 'common/hooks';
import Storage from 'common/services/Storage/Storage';

import { OrderBookDto } from 'components/OrderBook/types';

import { postDerivativeOrder } from 'views/derivatives/redux/derivatives.api';
import { DerivativeInvokeEnum, DerivativeSocketConfigType } from 'views/derivatives/types';

import { actionInvokeSignalR } from 'blocks/SocketProvider/redux/socket.actions';
import { sharedID } from 'blocks/SocketProvider/constants';

const weightedAverage = (nums: number[], weights: number[]) => {
  const [sum, weightSum] = weights.reduce(
    (acc, w, i) => {
      acc[0] += nums[i] * w;
      acc[1] += w;
      return acc;
    },
    [0, 0],
  );
  return sum / weightSum;
};

export function getPriceForRequestedAmount(
  amount: number | undefined,
  orderBook: Map<number, number>,
): number | undefined {
  if (amount !== undefined) {
    let weightedSum = 0;
    let remainingAmount = amount;
    let leftoverPrice = 0;

    for (const [rowPrice, rowAmouunt] of orderBook) {
      const availableAmount = Math.min(rowAmouunt, remainingAmount);
      weightedSum += availableAmount * rowPrice;
      remainingAmount -= availableAmount;

      if (remainingAmount <= 0) {
        break;
      }
      leftoverPrice = rowPrice;
    }
    if (remainingAmount) weightedSum += remainingAmount * leftoverPrice;

    return weightedSum / amount;
  }
  return undefined;
}

export function getPriceForRequestedCost(
  total: number | undefined,
  orderBook: Map<number, number>,
): number | undefined {
  if (total !== undefined) {
    const prices: number[] = [];
    const amounts: number[] = [];
    let cost = 0;
    let leftoverPrice = 0;
    let totalAmmount = 0;

    for (const [price, amount] of orderBook.entries()) {
      const remainingCost = total - cost;

      const rowCost = amount * price;

      if (rowCost >= remainingCost) {
        prices.push(price);
        amounts.push(amount);
        totalAmmount += amount;
        return weightedAverage(prices, amounts);
      }

      if (rowCost < remainingCost) {
        prices.push(price);
        amounts.push(amount);
        totalAmmount += amount;
        cost += price * amount;
      }

      if (cost === total) return weightedAverage(prices, amounts);

      leftoverPrice = price;
    }
    if (cost < total) {
      prices.push(leftoverPrice);
      amounts.push(total - totalAmmount);
      return weightedAverage(prices, amounts);
    }
  }
  return undefined;
}

const storage = new Storage('FUTURES:ORDERBOOK');

export const DerivativeDashboardContext = React.createContext<{
  orderBook: OrderBookDto & {
    bestBuy: (params: { quantity?: number | null; total?: number | null }) => number | undefined;
    bestSell: (params: { quantity?: number | null; total?: number | null }) => number | undefined;
    liquidity: {
      buy: number;
      sell: number;
    };
  };
  precession: string | number;
  base: string;
  quoted: string;
  code: string;
  digitsForBase: number;
  digitsForQuoted: number;
  info: {
    minQuantity: number;
    maxMarketQuantity: number;
    maxLimitQuantity: number;
    maxStopMarketQuantity: number;
    maxStopLimitQuantity: number;
    maxLeverage: number;
    contractValue: number;
    contractType: TradingPairContractType;
  };
  orders: {
    pending: DerivativesOrderProjection[];
    isPendingBuyOrderExist: boolean;
    isPendingSellOrderExist: boolean;
    isAnyOrderExist: boolean;
  };
  positions: {
    opened: DerivativesOpenedPositionProjection[];
    isPendingBuyPositionExist: boolean;
    isPendingSellPositionExist: boolean;
    isAnyPositionExist: boolean;
  };
  postOrder: (_order: CreateDerivativesOrderRequest) => Promise<void>;
}>({
  orderBook: {
    buy: new Map(),
    sell: new Map(),
    version: 0,
    prevVersion: 0,
    bestBuy: () => 0,
    bestSell: () => 0,
    liquidity: {
      buy: 0,
      sell: 0,
    },
  },
  base: '',
  quoted: '',
  code: '',
  precession: 0,
  digitsForBase: 0,
  digitsForQuoted: 0,
  info: {
    minQuantity: 0,
    maxMarketQuantity: 0,
    maxLimitQuantity: 0,
    maxStopMarketQuantity: 0,
    maxStopLimitQuantity: 0,
    maxLeverage: 0,
    contractValue: 0,
    contractType: TradingPairContractType.LINEAR,
  },
  orders: {
    pending: [],
    isPendingBuyOrderExist: false,
    isPendingSellOrderExist: false,
    isAnyOrderExist: false,
  },
  positions: {
    opened: [],
    isPendingBuyPositionExist: false,
    isPendingSellPositionExist: false,
    isAnyPositionExist: false,
  },
  postOrder: (_order: CreateDerivativesOrderRequest) => new Promise<any>((resolve) => resolve([])),
});

export const DerivativeDashboardProvider: React.FC<
  React.PropsWithChildren<{ base: string; quoted: string; code: string }>
> = React.memo(({ children, base, quoted, code: propsCode }) => {
  const dispatch = useDispatch();
  const { orders: pendingOrders } = useDerivativesOrders(({ tradingPairCode }) => tradingPairCode === propsCode);
  const { positions: openedPositions } = useDerivativesPositions(
    ({ tradingPairCode }) => tradingPairCode === propsCode,
  );
  const { orders: allPendingOrders } = useDerivativesOrders();
  const { positions: allPendingPositions } = useDerivativesPositions();

  const [isPendingBuyOrderExist, setIsPendingBuyOrderExist] = useState(false);
  const [isPendingSellOrderExist, setIsPendingSellOrderExist] = useState(false);
  const [isAnyOrderExist, setIsAnyOrderExist] = useState(false);
  const [isAnyPositionExist, setIsAnyPositionExist] = useState(false);

  const [isPendingBuyPositionExist, setIsPendingBuyPositionExist] = useState(false);
  const [isPendingSellPositionExist, setIsPendingSellPositionExist] = useState(false);

  const {
    info: {
      minQuantity,
      maxMarketQuantity,
      maxLimitQuantity,
      maxStopMarketQuantity,
      maxStopLimitQuantity,
      digitsForBase,
      digitsForQuoted,
    },
    stream: { maxLeverage = 0, code, contractValue = 0, contractType = TradingPairContractType.LINEAR },
  } = useDerivatives(propsCode);

  const { buy, sell, version, prevVersion } = useSelector(
    (state: DefaultRootState) => state.derivativesReducer.orderbook[String(code)],
  ) || { buy: new Map(), sell: new Map(), version: 0, prevVersion: 0 };

  const subscribeCode = useMemo(() => String(code), [code]);

  const baseConfig: DerivativeSocketConfigType = useMemo(
    () => ({
      hub: 'hubs/shared',
      invokeType: DerivativeInvokeEnum.SUBSCRIBE_DERIVATIVE_ORDER_BOOK,
      subscribeId: sharedID,
      data: [subscribeCode],
      hubEvents: [
        DerivativeInvokeEnum.RECEIVE_DERIVATIVE_ORDER_BOOK_SNAPSHOT,
        DerivativeInvokeEnum.RECEIVE_DERIVATIVE_ORDER_BOOK_UPDATE,
      ],
      id: `derivative-orderbook-${subscribeCode}`,
    }),
    [subscribeCode],
  );

  const subscribeConfig = useMemo(
    () => ({
      ...baseConfig,
      invokeType: DerivativeInvokeEnum.SUBSCRIBE_DERIVATIVE_ORDER_BOOK,
    }),
    [baseConfig],
  );
  const unsubscribeConfig = useMemo(
    () => ({
      ...baseConfig,
      invokeType: DerivativeInvokeEnum.UNSUBSCRIBE_DERIVATIVE_ORDER_BOOK,
      ref: subscribeConfig.id,
    }),
    [baseConfig, subscribeConfig.id],
  );

  useEffect(
    function listenPendingOrders() {
      const isBuyExist = !!pendingOrders.find((i) => i.side === DerivativesOrderSide.BUY);
      const isSellExist = !!pendingOrders.find((i) => i.side === DerivativesOrderSide.SELL);
      setIsPendingBuyOrderExist(isBuyExist);
      setIsPendingSellOrderExist(isSellExist);
      setIsAnyOrderExist(!!allPendingOrders.length);
    },
    [allPendingOrders.length, code, pendingOrders],
  );

  useEffect(
    function listenOpenedPositions() {
      const isBuyExist = !!openedPositions.find(
        ({ side, tradingPairCode }) => side === DerivativesPositionSide.BUY && tradingPairCode === code,
      );
      const isSellExist = !!openedPositions.find(
        ({ side, tradingPairCode }) => side === DerivativesPositionSide.SELL && tradingPairCode === code,
      );
      setIsPendingBuyPositionExist(isBuyExist);
      setIsPendingSellPositionExist(isSellExist);
      setIsAnyPositionExist(!!allPendingPositions.length);
    },
    [allPendingPositions.length, code, openedPositions],
  );

  const postOrder = useCallback(async (_order: CreateDerivativesOrderRequest) => {
    await postDerivativeOrder(_order);
  }, []);

  useEffect(
    function updateOrderbookSnapshot() {
      if (!!version && version !== prevVersion + 1) {
        dispatch(actionInvokeSignalR(subscribeConfig));
      }
    },
    [dispatch, prevVersion, subscribeConfig, version],
  );

  useEffect(() => {
    if (!storage.isExist(subscribeCode)) {
      dispatch(actionInvokeSignalR(subscribeConfig));
    }
    storage.add(subscribeCode);

    return () => {
      if (storage.isLast(subscribeCode)) {
        dispatch(actionInvokeSignalR(unsubscribeConfig));
      }

      storage.remove(subscribeCode);
    };
  }, [dispatch, subscribeCode, subscribeConfig, unsubscribeConfig]);

  const bestBuy = useCallback(
    ({ quantity, total }: { quantity?: number | null; total?: number | null }) => {
      if (quantity) return getPriceForRequestedAmount(quantity, buy);
      if (total) return getPriceForRequestedCost(total, buy);

      return undefined;
    },
    [buy],
  );

  const bestSell = useCallback(
    ({ quantity, total }: { quantity?: number | null; total?: number | null }) => {
      if (quantity) return getPriceForRequestedAmount(quantity, sell);
      if (total) return getPriceForRequestedCost(total, sell);

      return undefined;
    },
    [sell],
  );

  const value = useMemo(
    () => ({
      orderBook: {
        buy,
        sell,
        version,
        prevVersion,
        bestBuy,
        bestSell,
        liquidity: {
          buy: Array.from([...buy.values()]).reduce((result, amount) => result + amount, 0),
          sell: Array.from([...sell.values()]).reduce((result, amount) => result + amount, 0),
        },
      },
      base,
      quoted,
      code: propsCode,
      precession: 1,
      digitsForBase,
      digitsForQuoted,

      info: {
        minQuantity,
        maxMarketQuantity,
        maxLimitQuantity,
        maxStopMarketQuantity,
        maxStopLimitQuantity,
        maxLeverage,
        contractValue,
        contractType,
      },
      orders: {
        pending: pendingOrders,
        isPendingBuyOrderExist,
        isPendingSellOrderExist,
        isAnyOrderExist,
      },
      positions: {
        opened: openedPositions,
        isPendingBuyPositionExist,
        isPendingSellPositionExist,
        isAnyPositionExist,
      },
      postOrder,
    }),
    [
      base,
      propsCode,
      bestBuy,
      bestSell,
      buy,
      contractValue,
      contractType,
      digitsForBase,
      digitsForQuoted,
      isAnyOrderExist,
      isAnyPositionExist,
      isPendingBuyOrderExist,
      isPendingBuyPositionExist,
      isPendingSellOrderExist,
      isPendingSellPositionExist,
      maxLeverage,
      maxLimitQuantity,
      maxMarketQuantity,
      maxStopLimitQuantity,
      maxStopMarketQuantity,
      minQuantity,
      openedPositions,
      pendingOrders,
      postOrder,
      prevVersion,
      quoted,
      sell,
      version,
    ],
  );

  return <DerivativeDashboardContext.Provider value={value}>{children}</DerivativeDashboardContext.Provider>;
});

export default DerivativeDashboardProvider;
